import { useState, useEffect } from 'react';

/**
 * Safe localStorage wrapper that handles iframe and third-party context restrictions
 * Provides graceful fallbacks when localStorage is not available
 */

interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  isAvailable(): boolean;
}

class SafeLocalStorage implements SafeStorage {
  private available: boolean;
  private fallbackStorage: Map<string, string>;

  constructor() {
    this.fallbackStorage = new Map();
    this.available = this.checkAvailability();
  }

  private checkAvailability(): boolean {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }

    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('localStorage not available, using in-memory fallback:', error);
      return false;
    }
  }

  isAvailable(): boolean {
    return this.available;
  }

  getItem(key: string): string | null {
    try {
      if (this.available && typeof window !== 'undefined') {
        return localStorage.getItem(key);
      }
      return this.fallbackStorage.get(key) || null;
    } catch (error) {
      console.warn(`Failed to get item from localStorage: ${key}`, error);
      return this.fallbackStorage.get(key) || null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      if (this.available && typeof window !== 'undefined') {
        localStorage.setItem(key, value);
      }
      // Always update fallback storage as backup
      this.fallbackStorage.set(key, value);
    } catch (error) {
      console.warn(`Failed to set item in localStorage: ${key}`, error);
      // Fallback to in-memory storage
      this.fallbackStorage.set(key, value);
    }
  }

  removeItem(key: string): void {
    try {
      if (this.available && typeof window !== 'undefined') {
        localStorage.removeItem(key);
      }
      this.fallbackStorage.delete(key);
    } catch (error) {
      console.warn(`Failed to remove item from localStorage: ${key}`, error);
      this.fallbackStorage.delete(key);
    }
  }

  clear(): void {
    try {
      if (this.available && typeof window !== 'undefined') {
        localStorage.clear();
      }
      this.fallbackStorage.clear();
    } catch (error) {
      console.warn('Failed to clear localStorage', error);
      this.fallbackStorage.clear();
    }
  }
}

// Create singleton instance
export const safeStorage = new SafeLocalStorage();

/**
 * Hook-compatible wrapper for use with usehooks-ts useLocalStorage
 * Provides the same interface but with iframe-safe fallbacks
 */
export function useSafeLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  const getStoredValue = (): T => {
    try {
      const item = safeStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Failed to parse localStorage value for key: ${key}`, error);
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(getStoredValue);

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      safeStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Failed to store value for key: ${key}`, error);
      // Still update React state even if storage fails
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
    }
  };

  // Sync with storage changes (for cross-tab communication when available)
  useEffect(() => {
    if (safeStorage.isAvailable()) {
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === key && e.newValue !== null) {
          try {
            setStoredValue(JSON.parse(e.newValue));
          } catch (error) {
            console.warn('Failed to sync storage change', error);
          }
        }
      };

      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }
  }, [key]);

  return [storedValue, setValue];
}

// Re-export for convenience
export default safeStorage;
